import { useEffect, useState } from 'react';

import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { FundsModal } from './components/FundsModal.tsx';
import { PnlCardModal } from './components/PnlCard.tsx';
import { Header } from './components/Header.tsx';
import { Toasts } from './components/Toasts.tsx';
import { Tour } from './components/Tour.tsx';
import { config, MARKETS } from './config.ts';
import { fmtPx } from './lib/format.ts';
import { DocsPage } from './pages/DocsPage.tsx';
import { HousePage } from './pages/HousePage.tsx';
import { LeaderboardPage } from './pages/LeaderboardPage.tsx';
import { PapurrPage } from './pages/PapurrPage.tsx';
import { PortfolioPage } from './pages/PortfolioPage.tsx';
import { StatsPage } from './pages/StatsPage.tsx';
import { TradePage } from './pages/TradePage.tsx';
import { useApp } from './state.tsx';

/** The beta or canary warning of `VITE_BETA_NOTICE`, dismissed per text */
function Notice() {
  const text = config.betaNotice;
  const key = `papurr:notice:${text}`;
  const [shown, setShown] = useState(() => {
    if (!text) return false;
    try {
      return localStorage.getItem(key) !== 'dismissed';
    } catch {
      return true;
    }
  });
  if (!shown) return null;
  return (
    <div className="notice" role="note">
      <span>{text}</span>
      <button
        className="x"
        aria-label="Dismiss"
        onClick={() => {
          setShown(false);
          try {
            localStorage.setItem(key, 'dismissed');
          } catch {
            // storage blocked: shown again next visit
          }
        }}
      >
        ×
      </button>
    </div>
  );
}

export function App() {
  const { route, markets } = useApp();
  const m = markets?.find((x) => x.id === route.market);

  // the tab title tracks the price on the trade page
  useEffect(() => {
    const sym = MARKETS[route.market]?.symbol;
    document.title =
      route.page === 'trade' && m?.mid
        ? `$${fmtPx(m.mid, m.inst.priceScale)} · ${sym} · ${config.appName}`
        : `${route.page[0]!.toUpperCase()}${route.page.slice(1)} · ${config.appName}`;
  }, [route, m?.mid, m?.inst.priceScale]);

  // a new page opens at its top, not where the previous one was scrolled to
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [route.page]);

  return (
    <div className="app">
      <Header />
      <Notice />
      <main className="main">
        {/* a page that throws shows its error in place; the header stays, and another page starts clean */}
        <ErrorBoundary key={route.page} area={`${route.page} page`}>
          {route.page === 'trade' ? (
            <TradePage />
          ) : route.page === 'portfolio' ? (
            <PortfolioPage />
          ) : route.page === 'house' ? (
            <HousePage />
          ) : route.page === 'leaderboard' ? (
            <LeaderboardPage />
          ) : route.page === 'stats' ? (
            <StatsPage />
          ) : route.page === 'docs' ? (
            <DocsPage />
          ) : (
            <PapurrPage />
          )}
        </ErrorBoundary>
      </main>
      <FundsModal />
      <PnlCardModal />
      <Tour />
      <Toasts />
    </div>
  );
}
